import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Pencil, Trash2, ShoppingBag } from "lucide-react";

function formatPrice(p, currency = "TRY") {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(p);
  } catch {
    return `${p} ${currency}`;
  }
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveImage(0);
    api
      .get(`/products/by-slug/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const remove = async () => {
    if (!window.confirm("Bu ürünü silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/products/${product.product_id}`);
      toast.success("Ürün silindi");
      navigate("/magaza");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi");
    }
  };

  if (loading) {
    return (
      <div className="k-container py-16">
        <div className="k-card h-96 animate-pulse" />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="k-container py-16 text-center" data-testid="product-detail-not-found">
        <p className="font-body text-ink-700">Bu ürün bulunamadı.</p>
        <Link to="/magaza" className="k-btn-secondary mt-6 inline-flex">
          <ArrowLeft className="mr-2 h-4 w-4" /> Mağazaya dön
        </Link>
      </div>
    );
  }

  const images = product.images?.length ? product.images : product.image_url ? [product.image_url] : [];
  const soldOut = product.product_type === "fiziksel" && !product.in_stock;
  const canBuy = !soldOut && !!product.shopier_url;

  return (
    <div className="k-container py-12 md:py-16">
      <Link to="/magaza" className="inline-flex items-center gap-2 font-body text-sm text-ink-500 hover:text-sage-700" data-testid="back-to-store-link">
        <ArrowLeft className="h-4 w-4" /> Mağazaya dön
      </Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-2">
        <div data-testid="product-gallery">
          <div className="relative aspect-square w-full overflow-hidden rounded-3xl bg-earth-400/15">
            {images.length > 0 ? (
              <img
                src={images[activeImage]}
                alt={product.title}
                className="h-full w-full object-cover"
                data-testid="product-main-image"
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-sage-500/20 to-earth-400/25" />
            )}
            <span
              className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-bold ${
                product.product_type === "dijital" ? "bg-sage-500 text-cream-50" : "bg-earth-400 text-cream-50"
              }`}
            >
              {product.product_type === "dijital" ? "Dijital" : "Fiziksel"}
            </span>
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2" data-testid="product-thumbnails">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${
                    activeImage === i ? "border-sage-500" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`product-thumbnail-${i}`}
                >
                  <img src={img} alt={`${product.title} ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <h1 className="font-display text-5xl text-ink-900" data-testid="product-detail-title">
            {product.title}
          </h1>
          {product.product_type === "fiziksel" && (
            <span
              className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                soldOut ? "bg-destructive/15 text-destructive" : "bg-sage-500/15 text-sage-700"
              }`}
              data-testid="product-detail-stock"
            >
              {soldOut ? "Tükendi" : "Stokta"}
            </span>
          )}
          <p className="mt-5 whitespace-pre-line font-body text-ink-700" data-testid="product-detail-description">
            {product.description}
          </p>

          <div className="mt-8 flex items-center justify-between rounded-2xl border border-cream-300 bg-cream-50 p-5">
            <span className="font-heading text-3xl font-bold text-ink-900" data-testid="product-detail-price">
              {formatPrice(product.price, product.currency)}
            </span>
            {canBuy ? (
              <a
                href={product.shopier_url}
                target="_blank"
                rel="noopener noreferrer"
                className="k-btn-primary"
                data-testid="product-detail-buy-btn"
              >
                <ShoppingBag className="mr-2 h-4 w-4" /> Satın Al
              </a>
            ) : (
              <button
                disabled
                className="k-btn-primary opacity-50 cursor-not-allowed"
                data-testid="product-detail-buy-btn"
              >
                {soldOut ? "Tükendi" : "Yakında"}
              </button>
            )}
          </div>

          {user?.role === "admin" && (
            <div className="mt-6 flex gap-2 border-t border-cream-300 pt-4">
              <Link
                to={`/magaza/${product.product_id}/duzenle`}
                className="k-btn-ghost text-sm text-ink-500"
                data-testid="product-detail-edit-btn"
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Düzenle
              </Link>
              <button onClick={remove} className="k-btn-ghost text-sm text-destructive" data-testid="product-detail-delete-btn">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Sil
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
